import { Avatar } from "@radix-ui/themes";
import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { uploadUrl } from "@/config/publicConfig";
import { Person, PersonById, Upload } from "../../../../types/types";
import { useRefreshPersons } from "../../../../hooks/useRefreshStateHooks/useRefreshPersons";
import { useFetch } from "../../../../hooks/useFetch";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { Button } from "../../../../ui/Button";
import { Separator } from "../../../../ui/Separator";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { DatePicker } from "../../../../ui/Datepicker/DatePicker";
import DeleteStaff from "./DeleteStaff";
import { useAppStore } from "../../../../stores/RootStore";
import { staffStatuses } from "../../../../enums";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { SwitchSM } from "../../../../ui/Switch/Switch";

type Props = {
  id: string | undefined;
  currentPerson: PersonById;
  updatePerson: Function;
  setCurrentPerson: Function;
};

const StaffMainTab = ({ id, currentPerson, updatePerson, setCurrentPerson }: Props) => {
  const {persons, setPersons} = useAppStore();
  const [imageId, setImageId] = useState(currentPerson.attributes.image.id);
  const [image, setImage] = useState<object>();
  const statusesList = staffStatuses.map((status) => {
    return { value: status, label: t("staff_" + status) };
  });

  const allPosts = useAppStore((state) => state.posts);
  const staffPostsList = allPosts?.map((post) => {
    return { value: post.id, label: post.attributes.name };
  });

  const allDivisions = useAppStore((state) => state.divisions);
  const divisionsList = allDivisions?.map((division) => {
    return { value: division.id, label: division.attributes.name };
  });
  let [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { register, handleSubmit, control, getValues } = useForm({
    defaultValues: {
      birth_date: currentPerson.attributes.birth_date ? new Date(currentPerson.attributes.birth_date) : undefined,
      first_name: currentPerson.attributes.first_name,
      image: "",
      last_name: currentPerson.attributes.last_name,
      middle_name: currentPerson.attributes.middle_name,
      personnel_number: currentPerson.attributes.personnel_number,
      status: currentPerson.attributes.status,
      post: currentPerson.attributes.post_id
      ? { value: currentPerson.attributes.post_id, label: currentPerson.attributes.post_name }
      : undefined,
      division: currentPerson.attributes.division_id
      ? { value: currentPerson.attributes.division_id, label: currentPerson.attributes.division_name }
      : undefined,
      leader: currentPerson.attributes.leader
    },
  });
  const [{ response: itemSaveResponse, isLoading: itemSaveIsLoading, error: itemSaveError }, doFetchItemSave] = useFetch(`/directories/personnels/${id}`);
  const [{ response: imageUploadResponse, isLoading: imageUploadIsLoading, error: imageUploadError }, doFetchImageUpload] = useFetch(`/uploads`, true, true);
  const [{ response: saveChangeResponse, isLoading: saveChangeIsLoading, error: saveChangeError }, doFetchSaveChange] = useFetch(`/directories/personnels/${id}/changes`);

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  const onSubmit = (formData: any) => {
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          middle_name: formData.middle_name,
          birth_date: formData.birth_date,
          personnel_number: formData.personnel_number,
          image: image
        },
      },
    });
  };

  const onFileChoice = (value: string | Blob | File) => {
    const formData = new FormData();
    formData.set("attachment", value);
    doFetchImageUpload({
      method: "POST",
      data: formData,
    });
  };

  useEffect(() => {
    if (!imageUploadResponse) {
      return;
    }
    const imageUploadData = imageUploadResponse.data as unknown as Upload;
    setImageId(imageUploadData.id);
    setImage(imageUploadData);
  }, [imageUploadResponse]);

  useEffect(() => {
    if (!itemSaveResponse) return;
    editPersonItem(itemSaveResponse);
  }, [itemSaveResponse]);


  const editPersonItem = (itemSaveResponse: { data: unknown }) => {
    const newCurrentItemData = itemSaveResponse.data as unknown as Person;
    const withoutCurrentItem = persons?.filter((person) => person.id != currentPerson?.id)!;
    setPersons(
      [...withoutCurrentItem, newCurrentItemData].sort((a, b) =>
        a.attributes.first_name.toLowerCase() > b.attributes.first_name.toLowerCase() ? 1 : -1
      )
    );
  };

  useEffect(() => {
    if (!saveChangeResponse) return;
    editPersonItemAfterChange();
  }, [saveChangeResponse]);

  const editPersonItemAfterChange = () => {
    const newCurrentItemData = currentPerson as unknown as Person;
    const withoutCurrentItem = persons?.filter((equipment) => equipment.id != currentPerson?.id)!;
    setPersons([...withoutCurrentItem, newCurrentItemData]);
  };

  function handleChangeStatus(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);

    let date = new Date().toISOString();
    setCurrentPerson({
      ...currentPerson,
      attributes: {
        ...currentPerson?.attributes,
        status: value?.value || null,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: {
        data: {
          status: value?.value || null,
          at: date,
        },
      },
    });
  }

  function handleChangePosition(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value);

    let date = new Date().toISOString();
    setCurrentPerson({
      ...currentPerson,
      attributes: {
        ...currentPerson?.attributes,
        post_name: value?.label || null,
        post_id: Number(value?.value) || null,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: {
        data: {
          post_id: value?.value || null,
          at: date,
        },
      },
    });
  }


  function handleChangeDivision(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value);
    
    let date = new Date().toISOString();
    setCurrentPerson({
      ...currentPerson,
      attributes: {
        ...currentPerson?.attributes,
        division_name: value?.label || null,
        division_id: value?.value || null,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: {
        data: {
          division_id: value?.value || null,
          at: date,
        },
      },
    });
  }

  function handleChangeLeader(value: boolean, onChange: { (...event: any[]): void; (arg0: any): void }) {
    onChange(value);
    let date = new Date().toISOString();
    setCurrentPerson({
      ...currentPerson,
      attributes: {
        ...currentPerson?.attributes,
        leader: value || null,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: {
        data: {
          leader: value || false,
          at: date,
        },
      },
    });
  }

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('equipment_settings')}</span>
        </div>
      </div>
      <ScrollArea>
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 space-y-4">
          <div className="flex space-x-6">
            <Avatar
              size="7"
              src={imageId ? uploadUrl(imageId) : ""}
              fallback={currentPerson.attributes.last_name[0] + currentPerson.attributes.first_name[0]}
            />
            <div className="flex flex-col space-y-2 items-start">
              <div className="font-medium text-sm">{imageId ? t('change_staff_avatar') : t('upload_staff_avatar')}</div>
              <label className="flex flex-col items-center justify-center h-full">
                <span className="text-base leading-normal bg-muted px-2 py-1 rounded cursor-pointer hover:bg-primary hover:text-white">{t('select_file')}</span>
                {/*@ts-ignore */}
                <input className="hidden" type="file" onChange={(evt) => onFileChoice(evt.target.files[0])} />
              </label>
              <div className="text-xs">{t('upload_avatar_description')}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("name")} />
                </div>
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("surname")} />
                </div>
              )}
            />
            <Controller
              name="middle_name"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("middlename")} />
                </div>
              )}
            />
            <Controller
              name="personnel_number"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("personnel_tabel_number")} />
                </div>
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("status")}</Label>
                  <SelectButton
                    label={t("status_no_choosen")}
                    options={statusesList}
                    value={value ? statusesList.find((s) => s.value == value)! : null}
                    onChange={(e) => handleChangeStatus(e, onChange)}
                  />
                </div>
              )}
            />
            <Controller
              name="post"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("post")}</Label>
                  <SelectButton
                    label={t("choose_position")}
                    options={staffPostsList!}
                    value={value?.value ? staffPostsList?.find((s) => String(s.value) == String(value.value))! : null}
                    onChange={(e) => handleChangePosition(e, onChange)}
                  />
                </div>
              )}
            />
            <Controller
              name="division"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("division")}</Label>
                  <SelectButton
                    label={t("choose_division")}
                    options={divisionsList!}
                    value={value?.value ? divisionsList?.find((s) => String(s.value) == String(value.value))! : null}
                    onChange={(e) => handleChangeDivision(e, onChange)}
                  />
                </div>
              )}
            />
            <Controller
              name="leader"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-x-4 mt-8 h-10 flex items-center">
                  <SwitchSM id="airplane-mode" checked={value} onCheckedChange={(e) => handleChangeLeader(e, onChange)} />
                  <Label htmlFor="airplane-mode">{t("leader")}</Label>
                </div>
              )}
            />
          </div>
        </form>
      </ScrollArea>
      <div className="mt-8 flex w-full justify-between px-6">
        <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
          {t("delete")}
        </Button>
        <Button variant={"default"} size={"sm"} type="button" onClick={handleSubmit(onSubmit)}>
          {t("save")}
        </Button>
      </div>

      {isDeleteOpen && <DeleteStaff id={id!} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />}
    </div>
  );
};

export default StaffMainTab;
